import {
    ISlideContentLayoutAnalyzerService,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import {
    TextLeafNode,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/text-leaf-node';
import { IBackgroundFillInfo } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    PageText,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    ISlideRenderStateManager,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { IPresentationData } from '@tencent/slide-sdk-model';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';

export interface ITemplateContentAnalyzerServiceProps {
    contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService;
    slideRenderStateManager: ISlideRenderStateManager;
}

export interface IVerifiedCoverTitle {
    ctrTitleLeaf: TextLeafNode | undefined;
    subTitleLeaf: TextLeafNode | undefined;
    indexLeaf: TextLeafNode | undefined;
    subIndexLeaf: TextLeafNode | undefined;
    pageModel?: ISlideModel;
}

export interface IVerifiedContentsTitle {
    ctrTitleLeaf: TextLeafNode | undefined;
    titleLeafList: TextLeafNode[] | undefined;
    subTitleLeafList: TextLeafNode[] | undefined;
    pageModel: ISlideModel;
}

export interface IAnalyzerElementBack {
    fillBackground: IBackgroundFillInfo | undefined | null;
    firstElement: IElementBaseModel | undefined;
    secondElement: IElementBaseModel | undefined;
    thirdElement: IElementBaseModel | undefined;
    pageModel: ISlideModel;
    relationLayoutIndex: number | undefined;
}

export interface IAnalyzerTemplateProps {
    templatePresentation: IPresentationModel;
    textResult?: PageText[]; // 如果给定了 textResult，不会再重新计算一份 textResult
    changeBackground?: boolean; // 在分析过程中是否按需修改背景
}

export type TContentsNumberType = 2 | 3 | 4 | 5 | 6;

export interface IAnalyzerTemplateOptions {
    /**
     * 需要解析的目录数量
     */
    contentsNumber: TContentsNumberType;
}

export interface IUserAnalyzerResult {
    userDocCoverResult: IVerifiedCoverTitle | undefined;
    userDocContentsResult: IVerifiedContentsTitle | undefined;
    userDocSectionResult: IVerifiedCoverTitle[];
    userDocBackCoverResult: IVerifiedCoverTitle | undefined;
    userDocContentResult: PageText[];
}

export interface ITemplateAnalyzerResult {
    // 封面分析结果
    templateCoverResult: IVerifiedCoverTitle;
    // 前言分析结果
    templateSubCoverResult?: IVerifiedCoverTitle;
    // 目录分析结果
    templateContentsResult?: IVerifiedContentsTitle;
    // 首个章节页分析结果
    templateSectionResult?: IVerifiedCoverTitle;
    // 后续章节页分析结果
    followingSection?: IVerifiedCoverTitle[];
    // 封底分析结果
    templateBackCoverResult?: IVerifiedCoverTitle;
    // 正文背景分析背景
    templateContentBackResult: IAnalyzerElementBack;
    // 主要文字色
    mainFontColor: ITDColor | undefined;
}

export interface ITemplateContentAnalyzerService {
    setDelegateService(service: ITemplateContentAnalyzerServiceProps);
    /**
     * 是否是一个背景类元素
     * @param props
     */
    isBackgroundElement(props: {
        element?: IElementBaseModel,
        slide: ISlideModel,
        isStrict: boolean, // 严格判断尺寸，背景类元素可以是一些logo啥的，在严格尺寸下，这些logo不被算为背景
        presentation: IPresentationModel,
    }): boolean

    /**
     * 分析用户文档
     * @param props
     */
    analyzerUserDocs(props: IAnalyzerTemplateProps): Promise<IUserAnalyzerResult>;

    /**
     * 分析标准模版文档
     * @param props
     */
    analyzerTemplate(props: IAnalyzerTemplateProps, option: IAnalyzerTemplateOptions): Promise<ITemplateAnalyzerResult>;

    /**
     * 获取文档的自定义色/主题色数量，可用于是否有配套主题
     * 目前，如果50%以上的填充类颜色是主题色的话，我们就认为这个文档带有配套主题
     * @param presentation
     */
    getPresentationColorInfo(presentation: IPresentationModel): IAnalyzerColorNumber;

    /**
     * 分析非标准模版文档
     * @param props
     */
    analyzerNonStandardTemplate(props: IAnalyzerTemplateProps): Promise<ITemplateAnalyzerResult>;

    /**
     * 根据presentationData创建一个uiData
     * @param presentationData
     */
    createTemplateUiDataService(presentationData: IPresentationData): Promise<IUiDataService>;

    /**
     * 根据presentationData获取分析结果
     */
    analyzerNonStandardTemplateByPresentationModel(
        presentationData: IPresentationData,
    ): Promise<ITemplateAnalyzerResult>;

    /**
     * 根据模版id获取分析结果
     */
    analyzerNonStandardTemplateByTemplateId(templateId: string): Promise<ITemplateAnalyzerResult>;
}

export interface IAnalyzerColorNumber {
    customColorNumber: number;
    themeColorNumber: number;
}

/**
 * 标准模版索引，按这个规范输出模版标准
 * 区分与用户文档内容分析，优先保证分类准确，因此按索引写死模版分类
 * 第1页封面
 * 第2-6页目录，对应目录页有多少项，目前支持2-6个目录项
 * 第7页分隔页
 * 最后一页封底（不体现在枚举中）
 */
export enum PageTypeIndex {
    cover = 0,
    twoContents = 1,
    threeContents = 2,
    fourContents = 3,
    fiveContents = 4,
    sixContents = 5,
    section = 6,
}

export interface IInfoBlock {
    id?: string;
    text?: string;
    width?: number;
    height?: number;
    x?: number;
    y?: number;
    model?: IElementBaseModel;
}

export interface IAutoTag {
    id?: string;
    serialNumber?: IInfoBlock;
    title?: IInfoBlock;
    content?: IInfoBlock;
    image?: IInfoBlock;
    list?: IAutoTag[];
}

export enum NonStandardPageTypeIndex {
    cover = 0,
    fourContents = 1,
    section = 2,
}

export interface IVerifiedSection {
    ctrTitleLeaf: TextLeafNode | undefined;
    subTitleLeaf: TextLeafNode | undefined;
    indexTitleLeaf: TextLeafNode | undefined;
}

export interface IVerifiedContent {
    // 正文里的章节标题
    sectionName?: TextLeafNode;
}

